import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, User, X, BarChart3, Target, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import PlayerForm from '../components/PlayerForm';

const Players = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [filter, setFilter] = useState({ search: '', role: '', team_id: '' });
  const [sorting, setSorting] = useState({ sort_by: 'name', sort_order: 'asc' });
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: players = [], isLoading } = useQuery({
    queryKey: ['players', filter, sorting],
    queryFn: () => {
      const cleanFilter = Object.fromEntries(
        Object.entries({ ...filter, ...sorting }).filter(([_, value]) => value && value.toString().trim() !== '')
      );
      return api.getPlayers(cleanFilter);
    }
  });
  const { data: teams = [] } = useQuery({ queryKey: ['teams'], queryFn: api.getTeams });

  const { data: playerStats, isLoading: statsLoading } = useQuery({
    queryKey: ['playerStats', selectedPlayer?.id],
    queryFn: () => api.getPlayerStats(selectedPlayer.id),
    enabled: !!selectedPlayer && showStatsModal
  });

  const createMutation = useMutation({
    mutationFn: api.createPlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['players'] });
      setShowForm(false);
      toast.success('Player created successfully!');
    },
    onError: () => toast.error('Failed to create player'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.updatePlayer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['players'] });
      setShowForm(false);
      setEditingPlayer(null);
      toast.success('Player updated successfully!');
    },
    onError: () => toast.error('Failed to update player'),
  });

  const deleteMutation = useMutation({
    mutationFn: api.deletePlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['players'] });
      toast.success('Player deleted successfully!');
    },
    onError: () => toast.error('Failed to delete player'),
  });

  const handleSubmit = (data) => {
    // Clean the data - convert empty strings to null and remove empty fields
    const cleanData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key, 
        value === '' || value === null ? null : value
      ]).filter(([_, value]) => value !== null)
    );
    
    if (editingPlayer) {
      updateMutation.mutate({ id: editingPlayer.id, data: cleanData });
    } else {
      createMutation.mutate(cleanData);
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleViewStats = (player) => {
    setSelectedPlayer(player);
    setShowStatsModal(true);
  };

  const closeStatsModal = () => {
    setShowStatsModal(false);
    setSelectedPlayer(null);
  };

  const getRoleColor = (role) => {
    const colors = {
      batsman: 'bg-green-100 text-green-800',
      bowler: 'bg-blue-100 text-blue-800',
      all_rounder: 'bg-purple-100 text-purple-800',
      wicket_keeper: 'bg-orange-100 text-orange-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cricket-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Players</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-cricket-green text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors w-full sm:w-auto justify-center"
        >
          <Plus className="h-4 w-4" />
          <span>Add Player</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Search players..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={filter.role}
              onChange={(e) => setFilter({ ...filter, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">All Roles</option>
              <option value="batsman">Batsman</option>
              <option value="bowler">Bowler</option>
              <option value="all_rounder">All Rounder</option>
              <option value="wicket_keeper">Wicket Keeper</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
            <select
              value={filter.team_id}
              onChange={(e) => setFilter({ ...filter, team_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">All Teams</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sorting.sort_by}
              onChange={(e) => setSorting({ ...sorting, sort_by: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="name">Name</option>
              <option value="age">Age</option>
              <option value="nationality">Nationality</option>
              <option value="created_at">Date Added</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <select
              value={sorting.sort_order}
              onChange={(e) => setSorting({ ...sorting, sort_order: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      </div>

      {showForm ? (
        <PlayerForm
          player={editingPlayer}
          teams={teams}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingPlayer(null);
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {players.map((player) => (
          <div key={player.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-cricket-green rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{player.name}</h3>
                  <p className="text-sm text-gray-600">{player.nationality}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleViewStats(player)}
                  className="text-green-500 hover:text-green-700 p-1"
                  title="View Stats"
                >
                  <BarChart3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleEdit(player)}
                  className="text-blue-500 hover:text-blue-700 p-1"
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(player.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Role:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(player.role)}`}>
                  {player.role.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Age:</span>
                <span className="text-sm font-medium">{player.age}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Jersey:</span>
                <span className="text-sm font-medium">{player.jersey_number || 'N/A'}</span>
              </div>
              {player.team_id && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Team:</span>
                  <span className="text-sm font-medium">
                    {teams.find(t => t.id === player.team_id)?.name || 'Unknown'}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
        </div>
      )}

      {!showForm && players.length === 0 && (
        <div className="text-center py-12">
          <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No players found</h3>
          <p className="text-gray-500 mb-4">Create your first player to get started</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-cricket-green text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors"
          >
            Create Player
          </button>
        </div>
      )}

      {/* Player Stats Modal */}
      {showStatsModal && selectedPlayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-cricket-green text-white p-4 md:p-6 rounded-t-xl">
              <div className="flex items-start justify-between">
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 md:h-8 md:w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold">{selectedPlayer.name}</h2>
                    <p className="text-green-100 text-sm md:text-base">{selectedPlayer.nationality} | #{selectedPlayer.jersey_number || 'N/A'}</p>
                    <span className="inline-block mt-1 px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs md:text-sm">
                      {selectedPlayer.role?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={closeStatsModal}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {statsLoading ? (
                <div className="flex justify-center items-center h-48">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cricket-green"></div>
                </div>
              ) : playerStats ? (
                <div className="space-y-6">
                  {/* Quick Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-blue-600">{playerStats.batting?.matches || 0}</p>
                      <p className="text-sm text-blue-800">Matches</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{playerStats.batting?.runs || 0}</p>
                      <p className="text-sm text-green-800">Total Runs</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{playerStats.bowling?.wickets || 0}</p>
                      <p className="text-sm text-purple-800">Wickets</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-orange-600">{playerStats.batting?.strike_rate || 0}</p>
                      <p className="text-sm text-orange-800">Strike Rate</p>
                    </div>
                  </div>

                  {/* Batting Stats */}
                  <div className="bg-gray-50 rounded-lg p-4 md:p-5">
                    <div className="flex items-center space-x-2 mb-4">
                      <Target className="h-5 w-5 text-green-600" />
                      <h3 className="text-base md:text-lg font-semibold text-gray-800">Batting Statistics</h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Innings</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.innings || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Runs</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.runs || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Balls Faced</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.balls_faced || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Highest Score</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.highest_score || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Average</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.average || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Strike Rate</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.strike_rate || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Fours</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.fours || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Sixes</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.sixes || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">50s</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.fifties || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">100s</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.hundreds || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Not Outs</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.batting?.not_outs || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* Bowling Stats */}
                  <div className="bg-gray-50 rounded-lg p-4 md:p-5">
                    <div className="flex items-center space-x-2 mb-4">
                      <Award className="h-5 w-5 text-blue-600" />
                      <h3 className="text-base md:text-lg font-semibold text-gray-800">Bowling Statistics</h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Innings</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.innings || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Overs</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.overs || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Maidens</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.maidens || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Runs Conceded</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.runs_conceded || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Wickets</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.wickets || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Best Bowling</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.best_bowling || '0/0'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Economy</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.economy_rate || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Average</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.average || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">5 Wicket Hauls</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.five_wickets || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">Wides</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.wide_balls || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-xs text-gray-500 uppercase">No Balls</p>
                        <p className="text-xl font-bold text-gray-800">{playerStats.bowling?.no_balls || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* No Stats Message */}
                  {playerStats.batting?.matches === 0 && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">No match statistics available yet.</p>
                      <p className="text-sm text-gray-500">Stats will appear once the player participates in matches.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">Unable to load statistics.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Players;
